var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faArrowDown, faChevronDown, faChevronUp, faSignInAlt, faSitemap, faTimes, } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useElementBounding } from "@vueuse/core";
import { BAlert, BButton, BCard, BCardBody, BCardHeader } from "bootstrap-vue";
import { storeToRefs } from "pinia";
import { computed, onUnmounted, ref, watch } from "vue";
import { JobProvider } from "@/components/providers";
import { useDatatypesMapper } from "@/composables/datatypesMapper";
import { useInvocationGraph } from "@/composables/useInvocationGraph";
import { useWorkflowStateStore } from "@/stores/workflowEditorStateStore";
import { withPrefix } from "@/utils/redirect";
import WorkflowInvocationSteps from "./WorkflowInvocationSteps.vue";
import Heading from "@/components/Common/Heading.vue";
import ExternalLink from "@/components/ExternalLink.vue";
import JobInformation from "@/components/JobInformation/JobInformation.vue";
import JobParameters from "@/components/JobParameters/JobParameters.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
import FlexPanel from "@/components/Panels/FlexPanel.vue";
import WorkflowGraph from "@/components/Workflow/Editor/WorkflowGraph.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'InvocationGraph',
    props: {
        invocation: null,
        workflow: null,
        isTerminal: { type: Boolean },
        isScheduled: { type: Boolean },
        zoom: { default: 0.75 },
        showMinimap: { type: Boolean, default: true },
        showZoomControls: { type: Boolean, default: true },
        initialX: { default: -40 },
        initialY: { default: -40 },
        isFullPage: { type: Boolean, default: false }
    },
    setup(__props) {
        const props = __props;
        library.add(faArrowDown, faChevronDown, faChevronUp, faSignInAlt, faSitemap, faTimes);
        const loadingGraph = ref(true);
        const initialLoading = ref(true);
        const errored = ref(false);
        const errorMessage = ref("");
        const showingJobId = ref(undefined);
        const pollTimeout = ref(null);
        const hideGraph = ref(false);
        const jobCard = ref(null);
        const loadedJobInfo = ref(null);
        const invocationRef = computed(() => props.invocation);
        const { datatypesMapper } = useDatatypesMapper();
        const workflowId = computed(() => { var _a; return (_a = props.workflow) === null || _a === void 0 ? void 0 : _a.id; });
        const workflowVersion = computed(() => { var _a; return (_a = props.workflow) === null || _a === void 0 ? void 0 : _a.version; });
        const { steps, storeId, loadInvocationGraph } = useInvocationGraph(invocationRef, workflowId.value, workflowVersion.value);
        // Equivalent to onMounted; this is where the graph is initialized, and the polling is started
        watch(() => workflowId.value, (wfId) => __awaiter(this, void 0, void 0, function* () {
            if (wfId) {
                yield pollInvocationGraph();
            }
        }), { immediate: true });
        const stateStore = useWorkflowStateStore(storeId.value);
        const { activeNodeId } = storeToRefs(stateStore);
        watch(() => props.zoom, () => (stateStore.scale = props.zoom), { immediate: true });
        // on loading steps, toggle the first one with state === "error" or last one if none are errored
        watch(() => initialLoading.value, (newVal) => {
            var _a, _b;
            if (!newVal && steps.value) {
                const errorStep = Object.values(steps.value).find((step) => step.state === "error");
                if (errorStep) {
                    activeNodeId.value = errorStep.id;
                }
                else if (props.isTerminal) {
                    activeNodeId.value = ((_b = (_a = Object.values(steps.value)) === null || _a === void 0 ? void 0 : _a.slice(-1)[0]) === null || _b === void 0 ? void 0 : _b.id) || null;
                }
            }
        }, { immediate: true });
        // when the graph is hidden/visible, reset the active node and showingJobId
        watch(() => hideGraph.value, () => {
            showingJobId.value = undefined;
            activeNodeId.value = null;
        });
        // scroll to the job card when it is loaded (only on invocation route)
        if (props.isFullPage) {
            watch(() => loadedJobInfo.value, (jobInfo) => __awaiter(this, void 0, void 0, function* () {
                if (jobInfo) {
                    scrollJobToView();
                }
            }), { immediate: true });
        }
        // properties for handling the flex-draggable steps panel
        const invocationContainer = ref(null);
        const { width: containerWidth } = useElementBounding(invocationContainer);
        const minWidth = computed(() => containerWidth.value * 0.3);
        const maxWidth = computed(() => 0.7 * containerWidth.value);
        onUnmounted(() => {
            clearTimeout(pollTimeout.value);
        });
        const initialPosition = computed(() => ({
            x: -props.initialX * props.zoom,
            y: -props.initialY * props.zoom,
        }));
        /** Updates and loads the invocation graph */
        function loadGraph() {
            var _a;
            return __awaiter(this, void 0, void 0, function* () {
                loadingGraph.value = true;
                errored.value = false;
                errorMessage.value = "";
                try {
                    yield loadInvocationGraph();
                    errored.value = false;
                }
                catch (error) {
                    if ((_a = error.response) === null || _a === void 0 ? void 0 : _a.data.err_msg) {
                        errorMessage.value = error.response.data.err_msg;
                    }
                    else {
                        errorMessage.value = error;
                    }
                    errored.value = true;
                }
                finally {
                    loadingGraph.value = false;
                    if (initialLoading.value) {
                        initialLoading.value = false;
                    }
                }
            });
        }
        /** Poll and load the invocation graph until the invocation is terminal */
        function pollInvocationGraph() {
            return __awaiter(this, void 0, void 0, function* () {
                yield loadGraph();
                if (!props.isTerminal) {
                    pollTimeout.value = setTimeout(pollInvocationGraph, 3000);
                }
                else {
                    clearTimeout(pollTimeout.value);
                }
            });
        }
        function scrollJobToView() {
            var _a;
            const jobCardHeader = (_a = jobCard.value) === null || _a === void 0 ? void 0 : _a.querySelector(".card-header");
            jobCardHeader === null || jobCardHeader === void 0 ? void 0 : jobCardHeader.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        function toggleActiveStep(stepId) {
            if (activeNodeId.value === stepId) {
                activeNodeId.value = null;
            }
            else {
                activeNodeId.value = stepId;
            }
        }
        return { __sfc: true, props, loadingGraph, initialLoading, errored, errorMessage, showingJobId, pollTimeout, hideGraph, jobCard, loadedJobInfo, invocationRef, datatypesMapper, workflowId, workflowVersion, steps, storeId, loadInvocationGraph, stateStore, activeNodeId, invocationContainer, containerWidth, minWidth, maxWidth, initialPosition, loadGraph, pollInvocationGraph, scrollJobToView, toggleActiveStep, faArrowDown, faSitemap, faTimes, FontAwesomeIcon, BAlert, BButton, BCard, BCardBody, BCardHeader, JobProvider, withPrefix, WorkflowInvocationSteps, Heading, ExternalLink, JobInformation, JobParameters, LoadingSpan, FlexPanel, WorkflowGraph };
    }
});
